// GtBethAdmin: Beth's conversations on generativetalent.co (/interviews/beth).
//
// Every conversation, typed and spoken, newest first: who it was (if they left
// details), what they asked, whether she booked a call, links to the client and
// role she created, call minutes and what the typed chat cost. Open one to read
// the whole transcript. 50 at a time, searchable, built for thousands.
// C1-BETH8 (28 Oct 2026)
// C1-BETH8B: "No details" instead of "Chat only" (spoken conversations too).
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthFetch, initials, LexaSwitch } from './GtLexaAdmin';

const STATUS = {
  booked: { label: 'Call booked', tone: 'green' },
  lead: { label: 'Details left', tone: 'amber' },
  open: { label: 'No details', tone: 'slate' },
  closed: { label: 'No details', tone: 'slate' },
};
function ukTime(v) {
  if (!v) return '';
  try { return new Date(v).toLocaleString('en-GB', { timeZone: 'Europe/London', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch (e) { return ''; }
}
function clockTime(v) {
  if (!v) return '';
  try { return new Date(v).toLocaleTimeString('en-GB', { timeZone: 'Europe/London', hour: '2-digit', minute: '2-digit' }); } catch (e) { return ''; }
}
function mins(s) {
  const n = Math.round((Number(s) || 0) / 60);
  return n < 1 ? 'under a minute' : n === 1 ? '1 minute' : n + ' minutes';
}
function pence(p) { const n = Number(p) || 0; return n >= 100 ? '£' + (n / 100).toFixed(2) : n.toFixed(1) + 'p'; }
function whoOf(c) {
  const l = c.lead || {};
  if (l.name || l.company) return [l.name, l.company].filter(Boolean).join(', ');
  return 'Visitor';
}

function Chips({ value, options, onChange, label }) {
  return (
    <div className="gtlx-chips" role="radiogroup" aria-label={label}>
      {options.map(([k, l]) => (
        <button key={k} type="button" role="radio" aria-checked={value === k} className={'gtlx-chip' + (value === k ? ' gtlx-chip--on' : '')} onClick={() => onChange(k)}>{l}</button>
      ))}
    </div>
  );
}

function ConversationModal({ id, authFetch, onClose }) {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let live = true;
    authFetch('/api/beth/conversations/' + id).then((x) => { if (live) setD(x); }).catch((e) => { if (live) setError(e.message); });
    return () => { live = false; };
  }, [authFetch, id]);
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const st = d ? (STATUS[d.status] || STATUS.open) : null;
  let lastCall = null;
  return (
    <div className="gtc-modal-backdrop" onClick={onClose}>
      <div className="gtc-modal gtlx-modal gtbt-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Conversation with Beth">
        {!d ? (
          <div className="gtc-modal-body">{error ? <p className="gtivl-err">{error}</p> : <p className="gtivl-muted">Loading…</p>}</div>
        ) : (
          <>
            <div className="gtlx-modal-h">
              <span className={'gtivl-av' + (d.lead ? ' gtbt-av' : ' gtbt-av gtbt-av--anon')}>{d.lead && d.lead.name ? initials(d.lead.name) : 'V'}</span>
              <div className="gtlx-modal-t">
                <b>{whoOf(d)}</b>
                <span>Started {ukTime(d.created_at)}{d.tz ? ' · their time zone ' + d.tz : ''}{d.page ? ' · on ' + d.page : ''}</span>
              </div>
              <span className={'gtivl-pill gtivl-pill--' + st.tone}>{st.label}</span>
              <button type="button" className="gtivl-x" onClick={onClose} aria-label="Close">×</button>
            </div>
            <div className="gtbt-view">
              <section className="gtbt-tx" aria-label="Transcript">
                {d.transcript.length === 0 && <p className="gtivl-muted">Nothing was said in this conversation.</p>}
                {d.transcript.map((m, i) => {
                  const callStart = m.via === 'voice' && m.call && m.call !== lastCall;
                  const callEnd = m.via !== 'voice' && lastCall !== null;
                  lastCall = m.via === 'voice' ? m.call : null;
                  return (
                    <div key={i} className="gtbt-turnwrap">
                      {callStart && <div className="gtbt-mark">Call {m.call} started {clockTime(m.at)}</div>}
                      {callEnd && <div className="gtbt-mark">Back to typing</div>}
                      <div className={'gtbt-turn gtbt-turn--' + m.who}>
                        <b>{m.who === 'beth' ? 'Beth' : (d.lead && d.lead.name ? d.lead.name.split(' ')[0] : 'Visitor')}</b>
                        <p>{m.text}</p>
                        <span>{clockTime(m.at)}{m.via === 'voice' ? ' · spoken' : ''}</span>
                      </div>
                    </div>
                  );
                })}
              </section>
              <aside className="gtbt-side">
                {d.booking && d.booking.label && (
                  <div className="gtiv-card gtbt-box gtbt-box--booked">
                    <span className="gtiv-k">Call booked</span>
                    <b>{d.booking.label}</b>
                    {d.booking.meet && <a className="gtiv-link" href={d.booking.meet} target="_blank" rel="noopener noreferrer">Video link</a>}
                  </div>
                )}
                <div className="gtiv-card gtbt-box">
                  <span className="gtiv-k">What they left</span>
                  {d.lead ? (
                    <dl className="gtbt-dl">
                      {d.lead.name && <><dt>Name</dt><dd>{d.lead.name}</dd></>}
                      {d.lead.email && <><dt>Email</dt><dd><a className="gtiv-link" href={'mailto:' + d.lead.email}>{d.lead.email}</a></dd></>}
                      {d.lead.company && <><dt>Company</dt><dd>{d.lead.company}</dd></>}
                      {d.lead.website && <><dt>Website</dt><dd>{d.lead.website}</dd></>}
                      {d.lead.role && <><dt>Hiring for</dt><dd>{d.lead.role}</dd></>}
                    </dl>
                  ) : <p className="gtivl-muted">No name or email. They chatted without leaving details.</p>}
                  {(d.client || d.role) && (
                    <div className="gtbt-links">
                      {d.role && <button type="button" className="gtc-btn-ghost" onClick={() => navigate('/roles/' + d.role.id)}>Open the role{d.role.title ? ': ' + d.role.title : ''}</button>}
                      {d.client && <button type="button" className="gtc-btn-ghost" onClick={() => navigate('/clients/' + d.client.id)}>Open the client{d.client.name ? ': ' + d.client.name : ''}</button>}
                    </div>
                  )}
                </div>
                <div className="gtiv-card gtbt-box">
                  <span className="gtiv-k">The conversation</span>
                  <div className="gtlx-stats gtbt-stats">
                    <div><b>{d.visitor_messages}</b><span>from them</span></div>
                    <div><b>{d.calls}</b><span>{d.calls === 1 ? 'call' : 'calls'}</span></div>
                    <div><b>{d.calls ? Math.max(1, Math.round(d.call_seconds / 60)) : 0}</b><span>call minutes</span></div>
                    <div><b>{pence(d.cost_pence)}</b><span>typed chat cost</span></div>
                  </div>
                  {d.calls_detail.length > 0 && (
                    <ul className="gtbt-calls">
                      {d.calls_detail.map((c) => (
                        <li key={c.call + '-' + c.at}>Call {c.call}: {mins(c.seconds)}{c.ended === 'visitor_left' ? ', they hung up' : c.ended === 'completed' ? ', Beth said goodbye' : c.ended === 'time_limit' ? ', reached the time limit' : ''}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </aside>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function GtBethAdmin({ getToken }) {
  const authFetch = useAuthFetch(getToken);
  const location = useLocation();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState(null);
  const [next, setNext] = useState(null);
  const [filter, setFilter] = useState('all');
  const [via, setVia] = useState('');
  const [q, setQ] = useState('');
  const [qLive, setQLive] = useState('');
  const [error, setError] = useState('');
  const [more, setMore] = useState(false);
  const viewId = new URLSearchParams(location.search).get('c');
  const seq = useRef(0);

  useEffect(() => { const t = setTimeout(() => setQLive(q.trim()), 300); return () => clearTimeout(t); }, [q]);
  const query = useCallback((before) => '/api/beth/conversations?filter=' + filter + (via ? '&via=' + via : '') + (qLive ? '&q=' + encodeURIComponent(qLive) : '') + (before ? '&before=' + encodeURIComponent(before) : ''), [filter, via, qLive]);
  const load = useCallback(async () => {
    const mine = ++seq.current;
    try {
      const r = await authFetch(query());
      if (mine !== seq.current) return;
      setRows(r.conversations); setNext(r.next); setError('');
    } catch (e) { if (mine === seq.current) setError(e.message); }
  }, [authFetch, query]);
  useEffect(() => { setRows(null); load(); }, [load]);
  useEffect(() => { authFetch('/api/beth/summary').then(setSummary).catch(() => {}); }, [authFetch]);
  // New conversations arrive while the screen is open.
  useEffect(() => { const t = setInterval(() => { if (!next || !rows || rows.length <= 50) load(); }, 60000); return () => clearInterval(t); }, [load, next, rows]);

  async function loadMore() {
    if (!next) return;
    setMore(true);
    try { const r = await authFetch(query(next)); setRows((o) => (o || []).concat(r.conversations)); setNext(r.next); } catch (e) { setError(e.message); } finally { setMore(false); }
  }
  const open = (id) => navigate('/interviews/beth?c=' + id);
  const close = useCallback(() => navigate('/interviews/beth'), [navigate]);

  return (
    <div className="gtc-wrap gtivs gtlx-admin gtbt-admin">
      <div className="gtc-head">
        <div>
          <h1 className="gtc-title">Beth</h1>
          <p className="gtc-sub">Every conversation with visitors on generativetalent.co, typed and spoken.</p>
        </div>
      </div>
      <LexaSwitch active="beth" />
      {error && <div className="gtiv-card gtiv-err">{error} <button type="button" className="gtiv-link" onClick={load}>Try again</button></div>}

      <section className="gtiv-card">
        <div className="gtlx-card-h">
          <span className="gtbt-orb" aria-hidden="true" />
          <div><b>This month</b><span>Conversations where the visitor said something or started a call.</span></div>
        </div>
        <div className="gtlx-stats">
          <div><b>{summary ? summary.conversations : '–'}</b><span>conversations</span></div>
          <div><b>{summary ? summary.booked : '–'}</b><span>calls booked</span></div>
          <div><b>{summary ? summary.leads : '–'}</b><span>left details</span></div>
          <div><b>{summary ? summary.calls : '–'}</b><span>voice calls</span></div>
          <div><b>{summary ? summary.call_minutes : '–'}</b><span>call minutes</span></div>
          <div><b>{summary ? pence(summary.chat_cost_pence) : '–'}</b><span>typed chat cost</span></div>
        </div>
      </section>

      <div className="gtlx-listbar gtbt-listbar">
        <div className="gtivl-filters" role="tablist">
          {[['all', 'All'], ['booked', 'Calls booked'], ['lead', 'Left details'], ['chat', 'No details']].map(([k, l]) => (
            <button key={k} type="button" role="tab" aria-selected={filter === k} className={'gtivl-filter' + (filter === k ? ' gtivl-filter--on' : '')} onClick={() => setFilter(k)}>{l}</button>
          ))}
        </div>
        <Chips label="Typed or spoken" value={via} onChange={setVia} options={[['', 'Typed and spoken'], ['voice', 'Had a call']]} />
        <input className="gtiv-in gtbt-search" placeholder="Search names, companies, roles or anything said" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search conversations" />
      </div>

      {rows === null ? <div className="gtiv-card gtiv-loading">Loading conversations…</div> : rows.length === 0 ? (
        <div className="gtiv-card gtivl-empty">
          <span className="gtbt-orb gtbt-orb--lg" aria-hidden="true" />
          <b>{qLive || filter !== 'all' || via ? 'Nothing matches' : 'No conversations yet'}</b>
          <span>{qLive || filter !== 'all' || via ? 'Try other words or another filter.' : 'When visitors talk to Beth on generativetalent.co, they appear here.'}</span>
        </div>
      ) : (
        <div className="gtivl-list">
          {rows.map((c) => {
            const st = STATUS[c.status] || STATUS.open;
            return (
              <button key={c.id} type="button" className="gtiv-card gtivl-row gtbt-row" onClick={() => open(c.id)}>
                <span className={'gtivl-av gtbt-av' + (c.lead && c.lead.name ? '' : ' gtbt-av--anon')}>{c.lead && c.lead.name ? initials(c.lead.name) : 'V'}</span>
                <span className="gtivl-row-t">
                  <b>{whoOf(c)}{c.lead && c.lead.role ? <em className="gtivl-for"> · {c.lead.role}</em> : null}</b>
                  <span>{c.first_text ? '“' + c.first_text + '”' : 'Started a call'}</span>
                  <span>
                    {ukTime(c.updated_at)}
                    {' · '}{c.visitor_messages} from them
                    {c.calls ? ' · ' + c.calls + (c.calls === 1 ? ' call, ' : ' calls, ') + mins(c.call_seconds) : ''}
                    {c.booking ? ' · ' + c.booking.label : ''}
                  </span>
                </span>
                {c.calls > 0 && <span className="gtbt-tag">Spoke</span>}
                <span className={'gtivl-pill gtivl-pill--' + st.tone}>{st.label}</span>
              </button>
            );
          })}
          {next && <button type="button" className="gtc-btn-ghost gtbt-more" disabled={more} onClick={loadMore}>{more ? 'Loading…' : 'Show 50 more'}</button>}
        </div>
      )}

      {viewId && <ConversationModal key={viewId} id={viewId} authFetch={authFetch} onClose={close} />}
    </div>
  );
}
